import { memoizeWeakValue } from './memoize';

export type BoltEnv = 'dev' | 'int' | 'stg' | 'prd';

type BoltStack = `bolt-${BoltEnv}`;

type DiscoStack =
  | 'sonic-test'
  | 'eu1-test'
  | 'eu2-test'
  | 'us1-test'
  | 'ap2-test'
  | 'eu1-prod'
  | 'eu2-prod'
  | 'eu3-prod'
  | 'us1-prod'
  | 'ap2-prod'
  | 'eu-int';

export interface BoltEnvironmentConfig {
  readonly source: 'bolt';
  readonly type: 'test' | 'int' | 'stage' | 'prod';
  readonly env: BoltEnv;
  readonly stack: BoltStack;
  readonly region: undefined;
  readonly subdivision: string | undefined;
  readonly ingress: string;
}

export interface DiscoEnvironmentConfig {
  readonly source: 'disco';
  readonly type: 'test' | 'int' | 'prod';
  readonly stack: DiscoStack | undefined;
  readonly region: string | undefined;
  readonly ingress: string;
}

export type EnvironmentConfig = BoltEnvironmentConfig | DiscoEnvironmentConfig;
export type EnvironmentSource = EnvironmentConfig['source'];
export type EnvironmentType = NonNullable<EnvironmentConfig['type']>;
export type EnvironmentStack = NonNullable<EnvironmentConfig['stack']>;

const DISCO_V1_URL_PATTERN =
  /(?<region>\w+)-(?<env>test|int|prod+)(?:-(?<ingress>studio))?\.disco-api\.com/;

const DISCO_V2_URL_PATTERN =
  /(?:(?<ingress>studio)\.)?(?<region>\w+)\.(?<env>test|int|prod)\.disco-api\.com/;

const BOLT_URL_PATTERN =
  /(?:(?<ingress>studio)\.)?(?:(?<subdivision>\w+-(?:amer|latam|emea|apac))\.)?(?:(?<env>dev|int|stg|prd)\.)?(?:api\.)?discomax\.com/;

function parseDiscoEnvironmentType(discoEnv: string) {
  switch (discoEnv) {
    case 'test':
    case 'int':
    case 'prod':
      return discoEnv;
    default:
      throw new TypeError(`Invalid Disco environment: ${discoEnv}`);
  }
}

function parseDiscoV1Hostname(hostname: string): DiscoEnvironmentConfig | null {
  const match = DISCO_V1_URL_PATTERN.exec(hostname);

  if (match?.groups == null) {
    return null;
  }

  const { ingress = 'default', region, env } = match.groups;

  if (region === undefined || env === undefined) {
    return null;
  }

  return {
    source: 'disco',
    type: parseDiscoEnvironmentType(env),
    stack: `${region}-${env}` as DiscoStack,
    region,
    ingress,
  };
}

function parseDiscoV2Hostname(hostname: string): DiscoEnvironmentConfig | null {
  const match = DISCO_V2_URL_PATTERN.exec(hostname);

  if (match?.groups == null) {
    return null;
  }

  const { ingress = 'default', region, env } = match.groups;

  if (region === undefined || env === undefined) {
    return null;
  }

  return {
    source: 'disco',
    type: parseDiscoEnvironmentType(env),
    stack: `${region}-${env}` as DiscoStack,
    region,
    ingress,
  };
}

function parseBoltEnvironmentType(boltEnv: string) {
  switch (boltEnv) {
    case 'dev':
      return 'test';
    case 'int':
      return 'int';
    case 'stg':
      return 'stage';
    case 'prd':
      return 'prod';
    default:
      throw new TypeError(`Invalid Bolt environment: ${boltEnv}`);
  }
}

function parseBoltHostname(hostname: string): BoltEnvironmentConfig | null {
  const match = BOLT_URL_PATTERN.exec(hostname);

  if (match?.groups == null) {
    return null;
  }

  const { ingress = 'default', subdivision, env = 'prd' } = match.groups;

  return {
    source: 'bolt',
    type: parseBoltEnvironmentType(env),
    env: env as BoltEnv,
    stack: `bolt-${env}` as BoltStack,
    region: undefined,
    subdivision,
    ingress,
  };
}

let environmentConfigFallback: EnvironmentConfig | undefined;

// This should only be used for testing!
export function setEnvironmentConfigFallback(
  fallback:
    | EnvironmentConfig
    | string
    | ((
        prevEnvironmentConfig: EnvironmentConfig | undefined,
      ) => EnvironmentConfig | string | undefined)
    | undefined,
): void {
  if (typeof fallback === 'function') {
    fallback = fallback(environmentConfigFallback);
  }

  if (typeof fallback === 'string') {
    fallback = parseEnvironmentConfig(fallback);
  }

  environmentConfigFallback = fallback;
  parseEnvironmentConfig.cache.clear();
}

export function getEnvironmentConfigFallback(): EnvironmentConfig | undefined {
  return environmentConfigFallback;
}

export const parseEnvironmentConfig = memoizeWeakValue(
  (hostname: string): EnvironmentConfig => {
    const environmentConfig =
      parseBoltHostname(hostname) ??
      parseDiscoV1Hostname(hostname) ??
      parseDiscoV2Hostname(hostname) ??
      environmentConfigFallback;

    if (environmentConfig === undefined) {
      throw new Error(`Failed to parse environment from ${hostname}!`);
    }

    return environmentConfig;
  },
);

export function getEnvironmentConfig(): EnvironmentConfig {
  return parseEnvironmentConfig(
    process.env.API_URL ?? globalThis.location?.origin,
  );
}

export function getStackName(): EnvironmentStack | undefined {
  return getEnvironmentConfig().stack;
}

/**
 * Do NOT use this for feature checking. Create a rule!
 * @deprecated
 */
export function getEnvironmentType(): EnvironmentType {
  return getEnvironmentConfig().type;
}

/**
 * This function is used to determine the region based on the stack name.
 * It will return the region as a string. E.g. us1, eu1, eu, ap2 or undefined.
 */
export function getEnvironmentRegion(): string | undefined {
  return getEnvironmentConfig().region;
}

export function getEnvironmentSource(): EnvironmentSource {
  return getEnvironmentConfig().source;
}

export function getBoltEnvironmentType(type: EnvironmentType): BoltEnv {
  switch (type) {
    case 'test':
      return 'dev';
    case 'int':
      return 'int';
    case 'stage':
      return 'stg';
    case 'prod':
      return 'prd';
    default:
      (type) satisfies never;
      throw new TypeError(`Invalid environment type: ${type}`);
  }
}

function createBoltHostname(env: BoltEnvironmentConfig): string {
  let url = '';

  if (env.ingress !== 'default') {
    url += env.ingress;
    url += '.';
  }

  if (env.subdivision !== undefined) {
    url += env.subdivision;
    url += '.';
  }

  url += env.env;
  url += '.api.discomax.com';

  return url;
}

function createDiscoHostname(env: DiscoEnvironmentConfig): string {
  const { stack, ingress } = env;

  if (stack === 'eu-int') {
    return 'studio.eu.int.disco-api.com';
  }

  const ingressSegment = ingress === 'default' ? '' : `-${ingress}`;

  return `${stack}${ingressSegment}.disco-api.com`;
}

export function createHostnameFromEnvironmentConfig(
  env: EnvironmentConfig,
): string {
  const { source } = env;
  switch (source) {
    case 'bolt':
      return createBoltHostname(env);
    case 'disco':
      return createDiscoHostname(env);
    default:
      source satisfies EnvironmentSource;
      throw new TypeError(`Invalid source: ${source}`);
  }
}

export function createContentOrchestratorUrl(env: EnvironmentConfig): string {
  const type = getBoltEnvironmentType(env.type);

  return `https://backoffice.${type}.api.discomax.com/prismatic-microapps/co-ui`;
}
